/**
 * Выбор цели обычной атаки героев (E3). Все варианты детерминированы: при равенстве — ближе к штабу, затем
 * меньший id (порядок появления).
 */
import { PERMILLE } from "@newfungame/contracts";
import { dist2 } from "../sim/math";
import type { HeroEntity, SimApi, ZombieEntity } from "../sim/types";

export type Targeting = (hero: HeroEntity, sim: SimApi) => ZombieEntity | null;

/** Живые зомби в радиусе атаки героя. */
export function inRange(hero: HeroEntity, sim: SimApi, range = hero.attack.range): ZombieEntity[] {
  return sim.zombiesInRadius(hero.x, hero.y, range);
}

function hqD2(z: ZombieEntity, sim: SimApi): number {
  return dist2(z.x, z.y, sim.hq.x, sim.hq.y);
}

/** Лучший по ключу `score` (больше — лучше), при равенстве — ближе к штабу, затем меньший id. */
export function bestBy(
  list: readonly ZombieEntity[],
  sim: SimApi,
  score: (z: ZombieEntity) => number,
): ZombieEntity | null {
  let best: ZombieEntity | null = null;
  let bs = 0;
  let bd = 0;
  for (const z of list) {
    const s = score(z);
    const d = hqD2(z, sim);
    if (best === null || s > bs || (s === bs && (d < bd || (d === bd && z.id < best.id)))) {
      best = z;
      bs = s;
      bd = d;
    }
  }
  return best;
}

/** Сколько живых зомби из `list` в радиусе `r` от `z` (включая его самого). */
export function crowdAround(z: ZombieEntity, list: readonly ZombieEntity[], r: number): number {
  const r2 = r * r;
  let n = 0;
  for (const q of list) if (dist2(z.x, z.y, q.x, q.y) <= r2) n++;
  return n;
}

/** Ближайший к штабу (как пушка и Gunner). */
export const nearestToHq: Targeting = (hero, sim) => sim.findTarget(hero.x, hero.y, hero.attack.range);

/** Самая густая толпа в радиусе: больше всего зомби в радиусе взрыва цели (Bomber). */
export const densestCrowd: Targeting = (hero, sim) => {
  const list = inRange(hero, sim);
  if (list.length <= 1) return list[0] ?? null;
  const r = Math.max(1, hero.attack.splashRadius);
  return bestBy(list, sim, (z) => crowdAround(z, list, r));
};

/** Самый быстрый (Sparky: мгновенная искра наказывает бегунов). */
export const fastest: Targeting = (hero, sim) => bestBy(inRange(hero, sim), sim, (z) => z.speed * z.speedPermille);

/** Сначала не замедленные (Frosty раскидывает лёд по разным целям), иначе ближайший к штабу. */
export const unslowedFirst: Targeting = (hero, sim) =>
  bestBy(inRange(hero, sim), sim, (z) => (z.speedPermille >= PERMILLE ? 1 : 0));

/**
 * Дополнительные цели для лишних снарядов: остальные зомби в радиусе по близости к штабу; не хватает —
 * повтор основной цели.
 */
export function extraTargets(hero: HeroEntity, sim: SimApi, primary: ZombieEntity, n: number): ZombieEntity[] {
  const others = inRange(hero, sim)
    .filter((z) => z !== primary)
    .sort((a, b) => hqD2(a, sim) - hqD2(b, sim) || a.id - b.id);
  const out: ZombieEntity[] = [];
  for (let i = 0; i < n; i++) out.push(others[i] ?? primary);
  return out;
}
